/*!
 * Copyright (C) Microsoft Corporation. All rights reserved.
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import * as React from 'react';
import MonacoEditor from '@monaco-editor/react';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { CommandName, } from './FormulaEditor.types';
import { addProvidersForModel, ensureLanguageRegistered, languageName } from './PowerFxSyntax';
import { ensureThemeSetup, themeName } from './PowerFxTheme';
export var editorFontFamily = "'Menlo', 'Consolas', monospace,sans-serif";
export var editorFontSize = 14;
/** The default option configuration that we use for all the Monaco code editors inside PowerFx. */
var defaultEditorOptions = {
    fontSize: editorFontSize,
    lineDecorationsWidth: 4,
    scrollbar: {
        vertical: 'auto',
        verticalScrollbarSize: 8,
        horizontal: 'auto',
        horizontalScrollbarSize: 8,
    },
    // This fixes the first time render bug, and handles additional resizes.
    automaticLayout: true,
    contextmenu: false,
    // Don't show a border above and below the current line in the editor.
    renderLineHighlight: 'none',
    lineNumbers: 'off',
    wordWrap: 'on',
    autoClosingBrackets: 'never',
    quickSuggestions: true,
    scrollBeyondLastLine: false,
    // Don't show the minimap (the scaled down thumbnail view of the code)
    minimap: { enabled: false },
    selectionClipboard: false,
    // Don't add a margin on the left to render special editor symbols
    glyphMargin: false,
    revealHorizontalRightPadding: 24,
    find: {
        seedSearchStringFromSelection: 'never',
        autoFindInSelection: 'never',
    },
    suggestOnTriggerCharacters: true,
    codeLens: false,
    // Don't allow the user to collapse the curly brace sections
    folding: false,
    formatOnType: true,
    fontFamily: editorFontFamily,
    wordBasedSuggestions: false,
    // This option helps to fix some of the overflow issues when using the suggestion widget in grid rows
    // NOTE: This doesn't work when it's hosted inside Fluent Callout control
    // More details in https://github.com/microsoft/monaco-editor/issues/2503
    fixedOverflowWidgets: true,
};
var getClassNames = classNamesFunction();
var FormulaEditorBase = /** @class */ (function (_super) {
    __extends(FormulaEditorBase, _super);
    function FormulaEditorBase(props) {
        var _this = _super.call(this, props) || this;
        _this._model = null;
        _this._editor = null;
        /** Manually resetting the editor text on context change calls onChange. We need to ignore the call to onChange in that case. */
        _this._ignoreOnChange = false;
        _this._subscriptions = [];
        _this._keyPressedToLeaveTextArea = false;
        _this._getCurrentCursorPosition = function () {
            var editor = _this._editor;
            var selection = editor.getSelection();
            var startPosition = selection.getStartPosition();
            var endPosition = selection.getEndPosition();
            var cursorStartIndex = _this._model.getOffsetAt(startPosition);
            var cursorEndIndex = _this._model.getOffsetAt(endPosition);
            return {
                position: editor.getPosition(),
                cursorStartIndex: cursorStartIndex,
                cursorEndIndex: cursorEndIndex,
            };
        };
        _this._onEditorDidMount = function (monacoEditor, monacoParam) {
            _this._model = monacoParam.editor.createModel(_this.props.defaultValue);
            monacoEditor.setModel(_this._model);
            _this._editor = monacoEditor;
            ensureLanguageRegistered(monacoParam, _this.props.syntaxParameters);
            ensureThemeSetup(monacoParam);
            _this._registerCommands(monacoParam);
            monacoParam.editor.setModelLanguage(_this._editor.getModel(), languageName);
            addProvidersForModel(_this._model, _this._provideCompletionItems, _this._provideSignatureHelp, _this._provideCodeAction);
            _this._disposeEditorSubscriptions();
            _this._subscriptions.push(monacoEditor.onDidBlurEditorWidget(_this._onBlur));
            _this._subscriptions.push(monacoEditor.onDidFocusEditorWidget(_this._onFocus));
            _this._subscriptions.push(monacoEditor.onDidChangeCursorPosition(_this._onDidChangeCursorPosition));
            _this._subscriptions.push(monacoEditor.onKeyDown(_this._onKeyDown));
            _this._subscriptions.push(monacoEditor.onKeyUp(_this._onKeyUp));
            _this._subscriptions.push(_this._model.onDidChangeContent(_this._onChange));
            _this._subscriptions.push(monacoEditor.onDidChangeCursorSelection(_this._onDidChangeCursorSelection));
            if (_this.props.focusOnMount) {
                monacoEditor.focus();
                // By default the cursor will move to the beginning. This looks bad and is annoying, so move it to the end.
                _this._moveCursorToEnd(_this._editor, _this._model);
            }
            if (_this.props.onEditorDidMount) {
                _this.props.onEditorDidMount(monacoEditor, monacoParam);
            }
        };
        _this._onChange = function (e) {
            if (_this._ignoreOnChange || !_this.props.onChange || !_this._model) {
                return;
            }
            _this.props.onChange(_this._model.getValue(), e);
        };
        _this._onContainerKeyDown = function (event) {
            if ((event.key === 'Enter' || event.key === ' ') &&
                _this._editor &&
                event.target === _this._boundaryRef.current) {
                // When focus is on the boundary allow users enter the editor region.
                _this._keyPressedToLeaveTextArea = false; // since we want to re-enter, we reset this
                _this._editor.focus();
                event.preventDefault();
                event.stopPropagation();
            }
            if (event.key === 'Tab') {
                // If Tab is pressed
                if (_this._keyPressedToLeaveTextArea) {
                    // Previous key was Esc to leave the text area
                    // Reset
                    _this._keyPressedToLeaveTextArea = false;
                    event.stopPropagation();
                }
                else if (_this._editor && event.target === _this._boundaryRef.current) {
                    // When focus is on the boundary allow users enter the editor region.
                    _this._editor.focus();
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
            if (event.key === 'ArrowUp' && event.target !== _this._boundaryRef.current) {
                // This is being done because of a conflict when the control is used in
                // Fabric Focus Zones that use arrow keys to move focus. If the editor
                // has focus we don't want fabric handling it.
                event.stopPropagation();
            }
            return true;
        };
        _this._provideCompletionItems = function () {
            var rest = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                rest[_i] = arguments[_i];
            }
            return __awaiter(_this, void 0, void 0, function () {
                var _a;
                return __generator(this, function (_b) {
                    if (!this.props.completionProvider) {
                        return [2 /*return*/, {
                                incomplete: false,
                                suggestions: [],
                            }];
                    }
                    return [2 /*return*/, (_a = this.props).completionProvider.apply(_a, __spread(rest))];
                });
            });
        };
        _this._provideSignatureHelp = function () {
            var rest = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                rest[_i] = arguments[_i];
            }
            return __awaiter(_this, void 0, void 0, function () {
                var _a;
                return __generator(this, function (_b) {
                    // todo: test dispose
                    if (!this.props.signatureHelpProvider) {
                        return [2 /*return*/, {
                                value: {
                                    signatures: [],
                                    activeSignature: 0,
                                    activeParameter: 0,
                                },
                                dispose: function () {
                                    return;
                                },
                            }];
                    }
                    return [2 /*return*/, (_a = this.props).signatureHelpProvider.apply(_a, __spread(rest))];
                });
            });
        };
        _this._provideCodeAction = function () {
            var rest = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                rest[_i] = arguments[_i];
            }
            return __awaiter(_this, void 0, void 0, function () {
                var _a;
                return __generator(this, function (_b) {
                    if (!this.props.codeActionProvider) {
                        return [2 /*return*/, {
                                actions: [],
                                dispose: function () {
                                    return;
                                },
                            }];
                    }
                    return [2 /*return*/, (_a = this.props).codeActionProvider.apply(_a, __spread(rest))];
                });
            });
        };
        _this._onBlur = function () {
            _this.setState({ focused: false });
            if (_this.props.onBlur) {
                var blurValue = '';
                if (_this._editor) {
                    blurValue = _this._editor.getValue();
                }
                _this.props.onBlur(blurValue);
            }
        };
        _this._onFocus = function () {
            _this.setState({ focused: true });
            if (_this.props.onFocus) {
                var focusValue = '';
                if (_this._editor) {
                    focusValue = _this._editor.getValue();
                }
                _this.props.onFocus(_this._getCurrentCursorPosition(), focusValue);
            }
        };
        _this._onKeyDown = function (event) {
            if (_this._boundaryRef.current &&
                (event.code === 'Escape' || (_this.props.leaveOnEnterKey && event.code === 'Enter' && !event.shiftKey))) {
                _this._boundaryRef.current.focus();
                event.preventDefault();
                event.stopPropagation();
                // record that we want to leave the formula bar
                _this._keyPressedToLeaveTextArea = true;
                _this._preventTextAreaFocus();
                return;
            }
            if (_this.props.onKeyDown) {
                _this.props.onKeyDown(event);
            }
        };
        _this._onKeyUp = function (event) {
            if (_this.props.onKeyUp) {
                _this.props.onKeyUp(event);
            }
        };
        _this._onDidChangeCursorPosition = function (event) {
            // Provide current position and value to the subscriber in order to guarantee
            // that the value they use is valid for the current position if the user is actively typing
            if (_this.props.onDidChangeCursorPosition) {
                _this.props.onDidChangeCursorPosition(_this._getCurrentCursorPosition(), _this._editor.getValue(), event);
            }
        };
        _this._onDidChangeCursorSelection = function (event) {
            // Provide current selection and value to the subscriber in order to guarantee
            // that the value they use is valid for the current selection if the user is actively typing
            if (_this.props.onDidChangeCursorSelection) {
                _this.props.onDidChangeCursorSelection(_this._getCurrentCursorPosition(), _this._editor.getValue(), event);
            }
        };
        _this._disposeEditorSubscriptions = function () {
            _this._subscriptions.forEach(function (element) { return element.dispose(); });
            _this._subscriptions = [];
        };
        _this.state = { focused: false };
        _this._boundaryRef = React.createRef();
        return _this;
    }
    FormulaEditorBase.prototype.render = function () {
        var _a = this.props, defaultValue = _a.defaultValue, width = _a.width, _b = _a.height, height = _b === void 0 ? 30 : _b, locked = _a.locked, className = _a.className, styles = _a.styles, theme = _a.theme, monacoEditorOptions = _a.monacoEditorOptions;
        var focused = this.state.focused;
        var classNames = getClassNames(styles, { className: className, theme: theme, locked: !!locked, focused: focused });
        return (React.createElement("div", { className: classNames.root, onKeyDown: this._onContainerKeyDown, ref: this._boundaryRef, role: "presentation", tabIndex: 0 },
            React.createElement("div", { className: classNames.editorBoundary },
                React.createElement(MonacoEditor, { language: languageName, theme: themeName, width: width, height: height, options: __assign(__assign({}, defaultEditorOptions), monacoEditorOptions), defaultValue: defaultValue, onMount: this._onEditorDidMount }))));
    };
    FormulaEditorBase.prototype.componentDidMount = function () {
        this._preventTextAreaFocus();
    };
    FormulaEditorBase.prototype.componentDidUpdate = function (prevProps) {
        // Determine whether the editor value needs to be reset.
        var lockedStateHasChanged = prevProps.locked !== this.props.locked;
        if (lockedStateHasChanged && this._editor) {
            this._editor.updateOptions({
                readOnly: this.props.locked,
            });
        }
    };
    FormulaEditorBase.prototype.componentWillUnmount = function () {
        this._disposeEditorSubscriptions();
    };
    FormulaEditorBase.prototype._preventTextAreaFocus = function () {
        if (this._boundaryRef.current) {
            var monacoInputTextarea = this._boundaryRef.current.querySelector('textarea');
            if (monacoInputTextarea) {
                // The editor MUST be manually focused, or the user will end up in an
                // inescapable element in keyboard navigation.
                monacoInputTextarea.tabIndex = -1;
            }
        }
    };
    FormulaEditorBase.prototype._registerCommands = function (monacoParam) {
        var _this = this;
        monacoParam.editor.registerCommand(CommandName.CodeActionApplied, function (_accessor, args) {
            if (_this.props.onCommandExecuted) {
                _this.props.onCommandExecuted(CommandName.CodeActionApplied, args);
            }
        });
    };
    /**
     * Moves the cursor to the end of the content.
     */
    FormulaEditorBase.prototype._moveCursorToEnd = function (monacoEditor, model) {
        var numberOfLines = model.getLineCount();
        var numberOfColumnsOnLastLine = model.getLineMaxColumn(numberOfLines);
        monacoEditor.setPosition({
            column: numberOfColumnsOnLastLine,
            lineNumber: numberOfLines,
        });
    };
    return FormulaEditorBase;
}(React.Component));
export { FormulaEditorBase };
//# sourceMappingURL=FormulaEditor.base.js.map